import { useSyncExternalStore } from 'react';

/**
 * One pending human-verification request at a time, shared between the fetch helper that
 * needs a token and the dialog that produces one. A second request while one is pending
 * joins the same promise, so two screens never stack two widgets.
 */
export class HumanVerificationCancelled extends Error {
  constructor() {
    super('Human verification cancelled');
    this.name = 'HumanVerificationCancelled';
  }
}

type Pending = {
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (e: Error) => void;
};

let pending: Pending | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

export const requestHumanVerification = (): Promise<string> => {
  if (pending) return pending.promise;
  let resolve!: (token: string) => void;
  let reject!: (e: Error) => void;
  const promise = new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  pending = { promise, resolve, reject };
  emit();
  return promise;
};

export const resolveHumanVerification = (token: string): void => {
  const current = pending;
  pending = null;
  emit();
  current?.resolve(token);
};

export const cancelHumanVerification = (): void => {
  const current = pending;
  pending = null;
  emit();
  current?.reject(new HumanVerificationCancelled());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getSnapshot = () => pending !== null;

export const isHumanVerificationPending = (): boolean => getSnapshot();

export const useHumanVerificationPending = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot);

export const _resetHumanVerificationForTests = (): void => {
  pending = null;
  listeners.clear();
};
